import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { StencilProvider } from '@amzn/stencil-react-components/context';
import { Col, Row, Spacer } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType
} from '@amzn/stencil-react-components/message-banner';
import { SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { H4, H5, Text } from '@amzn/stencil-react-components/text';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CreateOrUpdateQueuesBuilder from 'src/builders/CreateOrUpdateQueuesBuilder';
import GetQueuesBuilder from 'src/builders/GetQueuesBuilder';
import GetUsersBuilder from 'src/builders/GetUsersBuilder';
import { UserContext } from 'src/components/Authentication/Authentication';
import AutoDismissMessageBanner from 'src/components/AutoDismissMessageBanner';
import ContentLoader from 'src/components/ContentLoader';
import { ListOfDropdownActions } from 'src/components/DropdownActions';
import EntityTable from 'src/components/EntityTable';
import LeftNavigationPanel from 'src/components/LeftNavigationPanel/LeftNavigationPanel';
import TableHeader from 'src/components/TableHeader';
import UploadFileModal from 'src/components/UploadFileModal';
import { TableRequiredFields } from 'src/configurations/InputElementConfigs';
import {
  ADD_USERS,
  ERROR_MSG_BANNER_AUTO_DISMISS_TIME,
  MessageBannerDetails,
  REMOVE_USERS,
  SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME
} from 'src/constants/DisplayConstants';
import { EntityType, SearchFilter } from 'src/constants/enums';
import { useAsyncState } from 'src/hooks/useAsyncState';
import { Queue } from 'src/models/Queue';
import { QueueUserOperationType } from 'src/models/ServiceRequests/CaseAuthorityServiceRequests';
import { User } from 'src/models/User';
import AddUsersToQueueModal from 'src/pages/CaseQueues/AddUsersToQueueModal';
import {
  downloadQueuesFileTemplate,
  processQueueFiles
} from 'src/pages/CaseQueues/QueueUpdateProcessingUtils';
import { redirectToFailurePage } from 'src/utils/ErrorRedirectUtils';
import { getUserByEmployeeIDInQueue } from 'src/utils/commons/CommonUtils';

const QueueViewWithUsersManagement = () => {
  const userContext = useContext(UserContext).authenticatedUser;
  const { domainId, queueId }: { domainId: string; queueId: string } =
    useParams();
  const [queueExists, setQueueExists] = React.useState(false);
  const [uploadAddModalIsOpen, setUploadAddModalIsOpen] = useState(false);
  const [uploadRemoveModalIsOpen, setUploadRemoveModalIsOpen] = useState(false);
  const queueDetails = useAsyncState<Queue>();
  const usersInDomain = useAsyncState<User[]>();
  const [messageBannerDetails, setMessageBannerDetails] =
    useState<MessageBannerDetails>({});
  const [messageBannerVisible, setMessageBannerVisible] = useState(false);
  const flipBannerVisibility = () => {
    setMessageBannerVisible(!messageBannerVisible);
  };

  const dropdownActionList: ListOfDropdownActions[] = [
    {
      actionName: ADD_USERS,
      executeAction: () => {
        setUploadAddModalIsOpen(true);
      }
    },
    {
      actionName: REMOVE_USERS,
      executeAction: () => {
        setUploadRemoveModalIsOpen(true);
      }
    }
  ];
  const getQueueDetails = async (providedQueueId) => {
    try {
      queueDetails.initiateAsyncState();
      const getQueuesResponse = await GetQueuesBuilder.getQueues(
        domainId,
        providedQueueId
      );
      if (getQueuesResponse.queues && getQueuesResponse.queues.length == 1) {
        queueDetails.setAsyncSuccess(getQueuesResponse.queues[0]);
        setQueueExists(true);
      } else {
        queueDetails.setAsyncSuccess({} as Queue);
      }
    } catch (error) {
      redirectToFailurePage(error, {
        source: 'QueueViewWithUsersManagement - getQueueDetails'
      });
      queueDetails.setAsyncFailure(error);
    }
  };

  const usersInQueue = useAsyncState<User[]>();
  const allUsersInQueue = useAsyncState<User[]>();

  const getUsersInQueue = async () => {
    try {
      usersInQueue.initiateAsyncState();
      const getUsersResponse = await GetUsersBuilder.getUsers(
        domainId,
        queueId
      );
      usersInQueue.setAsyncSuccess(getUsersResponse.users);
    } catch (error) {
      redirectToFailurePage(error, {
        source: 'QueueViewWithUsersManagement - getUsersInQueue'
      });
      usersInQueue.setAsyncFailure(error);
    }
  };

  const getAllUsersInQueue = async () => {
    try {
      allUsersInQueue.initiateAsyncState();
      const getUsersResponse = await GetUsersBuilder.getUsers(
        domainId,
        queueId,
        undefined,
        true
      );
      allUsersInQueue.setAsyncSuccess(getUsersResponse.users);
    } catch (error) {
      redirectToFailurePage(error, {
        source: 'QueueViewWithUsersManagement - getAllUsersInQueue'
      });
      allUsersInQueue.setAsyncFailure(error);
    }
  };

  const getUsersInDomain = async () => {
    try {
      usersInDomain.initiateAsyncState();
      const getUsersResponse = await GetUsersBuilder.getUsers(domainId);
      usersInDomain.setAsyncSuccess(getUsersResponse.users);
    } catch (error) {
      redirectToFailurePage(error, {
        source: 'QueueViewWithUsersManagement - getUsersInDomain'
      });
      usersInDomain.setAsyncFailure(error);
    }
  };

  useEffect(() => {
    void getQueueDetails(queueId).then();
    void getUsersInQueue().then();
    void getUsersInDomain().then();
    void getAllUsersInQueue().then();
  }, []);

  const getUsersInDomainAndNotInQueue = (): User[] => {
    if (usersInDomain.data && usersInQueue.data) {
      return usersInDomain.data!.filter(
        (user) => !usersInQueue.data!.includes(user)
      );
    } else {
      return [];
    }
  };
  const getUsersForSearchFilter = async (filterName, filterValue) => {
    if (filterName == SearchFilter.EMPLOYEE_ID) {
      await getUserByEmployeeIDInQueue(
        filterValue,
        queueId,
        queueDetails.data?.name,
        domainId,
        usersInQueue
      );
    }
  };

  const removeUsers = async (userIds: string[]) => {
    try {
      const queueDetailsCopy = { ...queueDetails.data };
      (queueDetailsCopy as any).userOperation = {
        type: QueueUserOperationType.REMOVE,
        userIds: userIds
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const editQueueResponse = await CreateOrUpdateQueuesBuilder.editQueue(
        domainId,
        userContext.alias,
        queueDetailsCopy
      );
      setMessageBannerDetails({
        messageBannerText: `Successfully removed user: ${userIds}`,
        messageBannerType: MessageBannerType.Success,
        messageBannerAutoDismiss: SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME
      });
      void getUsersInQueue();
      flipBannerVisibility();
    } catch (error) {
      setMessageBannerDetails({
        messageBannerText: `User removal failed with error: ${error.errorMessage}`,
        messageBannerType: MessageBannerType.Error,
        messageBannerAutoDismiss: ERROR_MSG_BANNER_AUTO_DISMISS_TIME
      });
      flipBannerVisibility();
    }
  };

  if (queueDetails.isLoading) {
    return (
      <ContentLoader
        size={SpinnerSize.Large}
        loadingText={'Loading Queue Details'}
        fontSize={'T300'}
      />
    );
  }

  const actionColumn = [
    {
      cellComponent: ({ data }) => (
        <Button
          id={'remove-user-button'}
          aria-describedby="sroDialog"
          variant={ButtonVariant.Tertiary}
          onClick={() => {
            void removeUsers([data.userId]).then();
            void getUsersInQueue().then();
          }}
        >
          Remove
        </Button>
      ),
      header: 'Action'
    }
  ];

  if (!queueExists) {
    return (
      <>
        <Row width={'100%'} justifyContent={'center'} flexWrap={'wrap'}>
          <Col justifyContent={'center'}>
            <MessageBanner type={MessageBannerType.Warning}>
              Queue with ID: {queueId} does not exist.
            </MessageBanner>
          </Col>
        </Row>
      </>
    );
  }

  // TODO: create grid like structure to pass queueDetails with row and column size.
  return (
    <Row>
      <LeftNavigationPanel />
      <Col width={'80%'}>
        {messageBannerVisible ? (
          <AutoDismissMessageBanner
            bannerText={messageBannerDetails.messageBannerText}
            bannerType={messageBannerDetails.messageBannerType}
            bannerAutoDismissTime={
              messageBannerDetails.messageBannerAutoDismiss
            }
            flipVisibility={flipBannerVisibility}
          />
        ) : null}
        <H4 textAlign={'center'}>{queueDetails.data?.name}</H4>
        <Spacer height={15} />
        <Row
          justifyContent={'center'}
          border={'2px solid neutral20'}
          margin={'30px'}
          padding={'1rem'}
        >
          <Col width={'33%'} gridGap={'5px'}>
            <H5>Queue Id</H5>
            <Text color={'neutral70'}>{queueDetails.data?.queueId}</Text>
          </Col>
          <Col width={'33%'} gridGap={'5px'}>
            <H5>Description</H5>
            <Text color={'neutral70'}>{queueDetails.data?.description}</Text>
          </Col>
          <Col width={'33%'}>
            <H5>Additional Attributes</H5>
            <Text color={'neutral70'}>
              {queueDetails.data?.additionalAttributes}
            </Text>
          </Col>
        </Row>
        <Col margin={'30px'}>
          <StencilProvider>
            {uploadAddModalIsOpen ? (
              <UploadFileModal
                onClose={() => setUploadAddModalIsOpen(false)}
                processFile={processQueueFiles(
                  domainId,
                  userContext.alias,
                  QueueUserOperationType.ADD
                )}
                downloadFileTemplate={downloadQueuesFileTemplate}
                onTableReload={getUsersInQueue}
              />
            ) : null}
            {uploadRemoveModalIsOpen ? (
              <UploadFileModal
                onClose={() => setUploadRemoveModalIsOpen(false)}
                processFile={processQueueFiles(
                  domainId,
                  userContext.alias,
                  QueueUserOperationType.REMOVE
                )}
                downloadFileTemplate={downloadQueuesFileTemplate}
                onTableReload={getUsersInQueue}
              />
            ) : null}
            <TableHeader
              entityName={EntityType.USER}
              dropdownActions={{
                listOfActions: dropdownActionList,
                csvExportEnabled: true,
                fileData: allUsersInQueue,
                fileName: 'user list'
              }}
              CustomCreateModal={
                <AddUsersToQueueModal
                  users={getUsersInDomainAndNotInQueue()}
                  queue={queueDetails.data!}
                  domainId={domainId}
                  onTableReload={getUsersInQueue}
                  setMessageBannerDetails={setMessageBannerDetails}
                  flipBannerVisibility={flipBannerVisibility}
                />
              }
              onTableReload={getUsersInQueue}
              searchByFilter={getUsersForSearchFilter}
            />
            {usersInQueue.isLoading ? (
              <ContentLoader
                size={SpinnerSize.Large}
                loadingText={'Loading Queue User'}
                fontSize={'T300'}
              />
            ) : (
              <EntityTable
                data={usersInQueue.data != undefined ? usersInQueue.data : []}
                requiredFields={TableRequiredFields[EntityType.USER]}
                customActionColumn={actionColumn}
                domainId={domainId}
                onTableReload={getUsersInQueue}
              />
            )}
          </StencilProvider>
        </Col>
      </Col>
    </Row>
  );
};

export default QueueViewWithUsersManagement;
